
import { useState, useMemo, useCallback } from 'react';
import { CTraderConnection } from '@/services/ctrader-token-refresh-service';

export interface PaginationConfig {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export function useCTraderPagination(
  connections: CTraderConnection[],
  searchQuery: string,
  statusFilter: string,
  pageSize: number = 50
) {
  const [currentPage, setCurrentPage] = useState(1);

  const filteredConnections = useMemo(() => {
    return connections.filter(conn => {
      const matchesSearch = searchQuery === '' || 
        conn.api_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conn.client_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (conn.csp_account_name && conn.csp_account_name.toLowerCase().includes(searchQuery.toLowerCase()));

      const isTokenExpired = conn.expire_date ? new Date(conn.expire_date) <= new Date() : false;
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'expired' && isTokenExpired) ||
        conn.status === statusFilter ||
        conn.refresh_status === statusFilter ||
        conn.balance_sync_status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [connections, searchQuery, statusFilter]);

  const totalPages = Math.ceil(filteredConnections.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedConnections = filteredConnections.slice(startIndex, endIndex);

  const paginationConfig: PaginationConfig = {
    page: currentPage,
    pageSize,
    total: filteredConnections.length,
    totalPages
  };

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);

  const goToNextPage = useCallback(() => goToPage(currentPage + 1), [goToPage, currentPage]);
  const goToPreviousPage = useCallback(() => goToPage(currentPage - 1), [goToPage, currentPage]);
  const goToFirstPage = useCallback(() => goToPage(1), [goToPage]);
  const goToLastPage = useCallback(() => goToPage(totalPages), [goToPage, totalPages]);

  // Reset to first page when filters change - with stable callback
  const resetPagination = useCallback(() => {
    console.log('resetPagination called - setting page to 1');
    setCurrentPage(1);
  }, []);

  return {
    paginatedConnections,
    paginationConfig,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    resetPagination,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1
  };
}
